/**
 * Create or edit one system achievement.
 */

import { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { ImageUploadZone } from '@components/common/ImageUploadZone'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'
import {
  CRITERIA_LABELS,
  formatThresholdMeasurement,
  fromThresholdInput,
  toThresholdInput,
  type CreateSystemAchievementRequest,
  type SystemAchievementSummaryDto,
} from '@api/types/systemAchievement'
import {
  useCreateSystemAchievement,
  useSystemAchievement,
  useUpdateSystemAchievement,
  useUploadSystemAchievementIcon,
} from '../../hooks/useSystemAchievements'

interface Props {
  open: boolean
  onClose: () => void
  /** The row being edited, or null to create. */
  achievement: SystemAchievementSummaryDto | null
}

const EMPTY: CreateSystemAchievementRequest = {
  name: '',
  shortDescription: '',
  fullDescription: '',
  criteriaType: 1,
  threshold: 1,
  points: 0,
  isActive: true,
  isHidden: false,
  displayOrder: 0,
}

/** Ordered for the dropdown: by the server's own grouping, which runs in tens. */
const CRITERIA_OPTIONS = Object.keys(CRITERIA_LABELS)
  .map(Number)
  .sort((a, b) => a - b)

export function SystemAchievementFormDialog({ open, onClose, achievement }: Props) {
  const isEdit = !!achievement

  // The list carries a summary; the long description only comes with the full record.
  const { data: full, isLoading: loadingFull } = useSystemAchievement(
    open && achievement ? achievement.id : undefined
  )

  const [form, setForm] = useState<CreateSystemAchievementRequest>(EMPTY)
  // A newly-picked icon file, staged until save — there is nowhere to upload it to until the
  // achievement (new or existing) has an id.
  const [iconFile, setIconFile] = useState<File | null>(null)
  const createMutation = useCreateSystemAchievement()
  const updateMutation = useUpdateSystemAchievement()
  const uploadIconMutation = useUploadSystemAchievementIcon()
  const error = createMutation.error ?? updateMutation.error ?? uploadIconMutation.error
  const mobileDialogProps = useMobileDialogProps()

  useEffect(() => {
    if (!open) return
    setIconFile(null)
    if (!achievement) {
      setForm(EMPTY)
      return
    }
    setForm({
      name: full?.name ?? achievement.name,
      shortDescription: full?.shortDescription ?? achievement.shortDescription,
      fullDescription: full?.fullDescription ?? '',
      criteriaType: full?.criteriaType ?? achievement.criteriaType,
      // Stored metres become typed kilometres for the two distance criteria; everything else is
      // stored exactly as it is typed and passes through untouched.
      threshold: toThresholdInput(
        full?.criteriaType ?? achievement.criteriaType,
        full?.threshold ?? achievement.threshold
      ),
      criteriaConfigJson: full?.criteriaConfigJson ?? null,
      points: full?.points ?? achievement.points,
      isActive: full?.isActive ?? achievement.isActive,
      isHidden: full?.isHidden ?? achievement.isHidden,
      displayOrder: full?.displayOrder ?? achievement.displayOrder,
    })
  }, [open, achievement, full])

  const criterion = CRITERIA_LABELS[form.criteriaType]
  const nameError = form.name.trim().length === 0
  const thresholdError = form.threshold < 1

  // Refs to the two description inputs, so "Insert measurement" can drop the token at the
  // cursor instead of always appending it to the end.
  const shortDescriptionRef = useRef<HTMLInputElement>(null)
  const fullDescriptionRef = useRef<HTMLTextAreaElement>(null)

  const THRESHOLD_TOKEN = '{threshold}'

  const insertThresholdToken = (
    field: 'shortDescription' | 'fullDescription',
    ref: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>
  ) => {
    const el = ref.current
    const current = form[field]
    const start = el?.selectionStart ?? current.length
    const end = el?.selectionEnd ?? current.length
    const next = `${current.slice(0, start)}${THRESHOLD_TOKEN}${current.slice(end)}`
    setForm({ ...form, [field]: next })
    if (el) {
      // The value updates on the next render; wait for it before moving the caret.
      requestAnimationFrame(() => {
        const pos = start + THRESHOLD_TOKEN.length
        el.focus()
        el.setSelectionRange(pos, pos)
      })
    }
  }

  /** What `{threshold}` resolves to for the player reading it, previewed in the admin's own unit. */
  const previewDescription = (text: string): string | null =>
    text.includes(THRESHOLD_TOKEN)
      ? text.replaceAll(
          THRESHOLD_TOKEN,
          formatThresholdMeasurement(form.criteriaType, form.threshold || 0)
        )
      : null

  /**
   * The id of an achievement this dialog has already created, so a retry does not create it twice.
   *
   * Create and icon upload are two requests, and the second can fail after the first has succeeded.
   * The dialog stays open showing the error, which is right — but without this, pressing the button
   * again would run the create again and leave a duplicate behind the one that is now only missing
   * its icon. Once created, a retry is an update.
   */
  const [createdId, setCreatedId] = useState<string | null>(null)
  useEffect(() => {
    if (open) setCreatedId(null)
  }, [open])

  const handleSave = async () => {
    if (nameError || thresholdError) return
    const existingId = achievement?.id ?? createdId
    // The one place the typed number becomes the stored one. Distance is typed in kilometres and
    // stored in metres; doing it here rather than in the field's onChange keeps the box showing
    // what was typed and leaves a single conversion to get wrong.
    const payload = {
      ...form,
      threshold: fromThresholdInput(form.criteriaType, form.threshold),
    }
    try {
      // Create/update first — the icon upload needs an id, and on create there isn't one yet.
      let id: string
      if (existingId) {
        await updateMutation.mutateAsync({ id: existingId, data: payload })
        id = existingId
      } else {
        const created = await createMutation.mutateAsync(payload)
        setCreatedId(created.id)
        id = created.id
      }
      if (iconFile) {
        await uploadIconMutation.mutateAsync({ id, file: iconFile })
      }
      onClose()
    } catch {
      // Surfaced through the mutations' own error state (see `error` above); nothing to add, and
      // letting it escape the click handler would only produce an unhandled rejection.
    }
  }

  const saving = createMutation.isPending || updateMutation.isPending || uploadIconMutation.isPending

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth {...mobileDialogProps}>
      <DialogTitle>{isEdit ? 'Edit Achievement' : 'New Achievement'}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error instanceof Error ? error.message : 'Could not save the achievement'}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Icon
            </Typography>
            <ImageUploadZone
              value={iconFile ? undefined : achievement?.iconUrl}
              onFileSelect={setIconFile}
              onClear={() => setIconFile(null)}
              maxSizeMB={2}
              label="Upload icon"
              description="Small square icon shown on the badge card. PNG recommended. You can also paste an image (Ctrl+V)."
              allowCamera={false}
              enableCropping={false}
              disabled={saving}
            />
          </Box>

          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            error={nameError}
            helperText={nameError ? 'Required' : ' '}
            fullWidth
            autoFocus
          />

          <Box>
            <TextField
              inputRef={shortDescriptionRef}
              label="Short description"
              value={form.shortDescription}
              onChange={(e) => setForm({ ...form, shortDescription: e.target.value })}
              helperText="One line, shown on the badge card."
              fullWidth
            />
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
              <Button
                size="small"
                onClick={() => insertThresholdToken('shortDescription', shortDescriptionRef)}
              >
                Insert measurement
              </Button>
              {previewDescription(form.shortDescription) && (
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>
                  Preview: {previewDescription(form.shortDescription)}
                </Typography>
              )}
            </Stack>
          </Box>

          <Box>
            <TextField
              inputRef={fullDescriptionRef}
              label="Full description"
              value={form.fullDescription}
              onChange={(e) => setForm({ ...form, fullDescription: e.target.value })}
              helperText={loadingFull ? 'Loading…' : 'HTML is allowed here.'}
              multiline
              minRows={2}
              fullWidth
            />
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
              <Button
                size="small"
                onClick={() => insertThresholdToken('fullDescription', fullDescriptionRef)}
              >
                Insert measurement
              </Button>
              {previewDescription(form.fullDescription) && (
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>
                  Preview: {previewDescription(form.fullDescription)}
                </Typography>
              )}
            </Stack>
          </Box>

          <TextField
            select
            label="Criterion"
            value={form.criteriaType}
            onChange={(e) => setForm({ ...form, criteriaType: Number(e.target.value) })}
            fullWidth
          >
            {CRITERIA_OPTIONS.map((value) => (
              <MenuItem key={value} value={value}>
                {CRITERIA_LABELS[value].label}
              </MenuItem>
            ))}
          </TextField>

          {/* The unit is the thing people get wrong. Distance is typed here in kilometres and
              stored in metres, so the box and the database disagree on purpose; the helper text
              under the field states the stored figure rather than leaving anyone to infer it. */}
          {criterion?.note && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
              {criterion.note}
            </Typography>
          )}

          <TextField
            label="Threshold"
            type="number"
            value={form.threshold}
            onChange={(e) => setForm({ ...form, threshold: Number(e.target.value) })}
            error={thresholdError}
            helperText={
              thresholdError
                ? 'Must be at least 1'
                : criterion?.storedIn
                  ? `Typed in ${criterion.unit}. Stored as ${fromThresholdInput(
                      form.criteriaType,
                      form.threshold || 0
                    ).toLocaleString()} ${criterion.storedIn}.`
                  : `Counted in ${criterion?.unit ?? 'units'}.`
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">{criterion?.unit ?? ''}</InputAdornment>
              ),
            }}
            fullWidth
          />

          {form.criteriaType === 6 && (
            <TextField
              label="Criteria config (JSON)"
              value={form.criteriaConfigJson ?? ''}
              onChange={(e) => setForm({ ...form, criteriaConfigJson: e.target.value || null })}
              helperText='This criterion needs one, e.g. {"CategoryId": 7}. Without it the achievement never progresses.'
              fullWidth
            />
          )}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Points"
              type="number"
              value={form.points}
              onChange={(e) => setForm({ ...form, points: Number(e.target.value) })}
              fullWidth
            />
            <TextField
              label="Display order"
              type="number"
              value={form.displayOrder}
              onChange={(e) => setForm({ ...form, displayOrder: Number(e.target.value) })}
              fullWidth
            />
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
            }
            label="Active — off means it accrues no progress at all"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={form.isHidden}
                onChange={(e) => setForm({ ...form, isHidden: e.target.checked })}
              />
            }
            label="Hidden — players do not see it until they earn it"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || nameError || thresholdError}>
          {saving ? 'Saving…' : isEdit || createdId ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
